import { yupResolver } from '@hookform/resolvers/yup';
import { Clock, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DELIVERY_WINDOW_TOOLTIP } from 'src/app/MyQuote/CreateQuote/Steps/Steps.const';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import { ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import DateSelect from 'src/components/DateSelect/DateSelect';
import DeleteModal from 'src/components/DeleteModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import MapBox from 'src/components/MapBox/MapBox';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setConsigneeOptions, setConsignees } from 'src/redux/address.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import {
    createDateWithoutTimezoneConversion,
    formatTimeInput,
    getDateWithSuffixFormat,
    getFormattedDateIgnoringTime,
    isShipperConsigneeAddressValid,
    collectFormErrorPaths,
    scrollToFirstFormError,
} from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import ShipperConsigneeForm from '../../../CreateQuote/Steps/ShipperConsigneeDetails/ShipperConsigneeForm';
import { ADD_QUOTE_KEYS } from '../../addQuote.const';
import { addQuoteConsigneeDetails, addQuoteRevalidateStep, addQuoteShipperDetails, setAddQuoteRevalidateStep } from '../../AddQuote.slice';
import { isPastCalendarDay } from '../../dateGuard.helpers';
import { DATE_TYPE, DELIVERY_DATE_TYPE_OPTIONS, DELIVERY_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from '../addQuoteSteps.const';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';
import { useRegisterStepperPersist } from '../useStepperPersist';

interface IProps {
    setHeaderChildren: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

interface IConsigneeDetailsForm {
    deliverySensitivity: string;
    deliveryDate: Date | string | null;
    deliveryEndDate: Date | string | null;
    deliveryWindowType: string;
    deliveryStartTime: string | null;
    deliveryEndTime: string | null;
    serviceId: number[];
    limitedAccessDelivery: boolean;
    limitedAccessDeliveryType: string;
    limitedAccessDeliveryOther: string;
    consigneeId: number | null;
    consigneeCompanyName: string;
    consigneeAddress: {
        fullAddress: string;
        address1?: string;
        city: string;
        country: string;
        countryCode?: string;
        latitude: string;
        longitude: string;
        postal?: string;
        state: string;
        stateCode?: string;
        streetNumber?: string;
        timezone?: string;
        additionalServiceIds?: number[];
    } | null;
}

const INITIAL_DELETE_ACTION = {
    mode: '',
    consignee: false,
    isContact: false,
    contactId: null as null,
};

const isBeforePickupWindowEnd = (value: Date | string | null | undefined, pickupWindowEndDate: string | null | undefined) => {
    if (!value || !pickupWindowEndDate) return false;

    return moment(value).format('YYYY-MM-DD') < moment(pickupWindowEndDate).format('YYYY-MM-DD');
};

const ConsigneeDetails: FC<IProps> = ({ setHeaderChildren }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { onSaveNext, onBack, onPersist } = useSaveNextStep();
    const { appearance, serviceTypes } = useAppSelector((state: RootState) => state.basic);
    const savedData = useAppSelector(addQuoteConsigneeDetails);
    const revalidateStep = useAppSelector(addQuoteRevalidateStep);
    const shipperData = useAppSelector(addQuoteShipperDetails);

    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const prevDeliveryWindowType = useRef<string | undefined>(undefined);

    const { shipperOptions, consigneeOptions, consignees, handleSearchConsignee } = useAddressDetails();

    const [isEditId, setIsEditId] = useState(false);
    const [deleteAction, setDeleteAction] = useState(INITIAL_DELETE_ACTION);
    const [isDeleting, setIsDeleting] = useState(false);
    const [preEditId, setPreEditId] = useState<number | null>(null);
    const [action, setAction] = useState({ deliveryService: false, limitedAccess: false });

    const todayDate = useMemo(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC')), [appearance?.timezone]);

    // Delivery cannot start before the pickup window CLOSES — for a flexible pickup that is pickupEndDate,
    // not pickupDate. Anchoring on the start date let users pick a delivery date inside the pickup window
    // (e.g. pickup 18–19 allowed a delivery of 18).
    const pickupWindowEndDate = useMemo(
        () => (shipperData?.pickupSensitivity === DATE_TYPE.FLEXIBLE && shipperData?.pickupEndDate ? shipperData.pickupEndDate : shipperData?.pickupDate),
        [shipperData?.pickupSensitivity, shipperData?.pickupEndDate, shipperData?.pickupDate]
    );

    // minDeliveryDate = max(today, pickup window end) — if the saved pickup date is in the past, delivery min must still be today
    const minDeliveryDate = useMemo(() => {
        if (!pickupWindowEndDate) return todayDate;

        const todayStr = moment.tz(appearance?.timezone || 'UTC').format('YYYY-MM-DD');
        const pickupStr = moment.utc(pickupWindowEndDate).format('YYYY-MM-DD');

        return moment(pickupStr).isSameOrAfter(todayStr, 'day') ? createDateWithoutTimezoneConversion(moment(pickupWindowEndDate)) : todayDate;
    }, [pickupWindowEndDate, todayDate, appearance?.timezone]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                deliverySensitivity: Yup.string().required('Delivery sensitivity is required'),
                deliveryDate: Yup.string()
                    .nullable()
                    .test('required-when-end-filled', t('QuotingHub.CreateQuote.Delivery_Start_Date_Required'), function (value) {
                        const { deliverySensitivity, deliveryEndDate } = this.parent;

                        if (deliverySensitivity === 'flexible' && deliveryEndDate) return !!value;

                        return true;
                    })
                    .test('not-before-pickup-window-end', t('ValidationMessages.Delivery_date_cannot_be_before_pickup_date'), function (value) {
                        const { deliverySensitivity } = this.parent;

                        if (deliverySensitivity === DATE_TYPE.OPEN) return true;

                        return !isBeforePickupWindowEnd(value, pickupWindowEndDate);
                    })
                    // A date already in the past can't be reached through the picker (minDeliveryDate
                    // floors at today), but it CAN arrive pre-filled — Duplicate Quote copies the
                    // source quote's delivery date verbatim, and a wizard left open ages into it.
                    // Shared with the final-step submit guard so both agree on what "past" means.
                    .test('not-in-past', t('ValidationMessages.Delivery_date_cannot_be_in_the_past'), function (value) {
                        const { deliverySensitivity } = this.parent;

                        if (deliverySensitivity === DATE_TYPE.OPEN) return true;

                        return !isPastCalendarDay(value, appearance?.timezone);
                    }),
                deliveryEndDate: Yup.string()
                    .nullable()
                    .when('deliverySensitivity', {
                        is: DATE_TYPE.FLEXIBLE,
                        then: (schema) =>
                            schema
                                .test('required-if-delivery-start-date', t('ValidationMessages.End_Date_is_required'), function (value) {
                                    const { deliveryDate } = this.parent;
                                    if (deliveryDate && !value) return false;

                                    return true;
                                })
                                .test('not-before-pickup-window-end', t('ValidationMessages.Delivery_date_cannot_be_before_pickup_date'), function (value) {
                                    return !isBeforePickupWindowEnd(value, pickupWindowEndDate);
                                })
                                .test('not-in-past', t('ValidationMessages.Delivery_date_cannot_be_in_the_past'), (value) => !isPastCalendarDay(value, appearance?.timezone)),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                deliveryWindowType: Yup.string().required('Delivery window type is required'),
                deliveryStartTime: Yup.string()
                    .nullable()
                    .test('required-when-end-filled', t('QuotingHub.CreateQuote.Delivery_Start_Time_Required'), function (value) {
                        const { deliveryWindowType, deliveryEndTime } = this.parent;

                        if (deliveryWindowType === WINDOW_TYPE.DELIVERY_TIME && deliveryEndTime) return !!value;

                        return true;
                    }),
                deliveryEndTime: Yup.string()
                    .notRequired()
                    .nullable()
                    .test('required-if-delivery-start-time', t('QuotingHub.CreateQuote.Delivery_End_Time_Required'), function (value) {
                        const { deliveryWindowType, deliveryStartTime } = this.parent;
                        if (deliveryWindowType !== WINDOW_TYPE.DELIVERY_TIME) return true;
                        if (deliveryStartTime && !value) return false;

                        return true;
                    })
                    .test('is-after-start', 'End time must be after start time', function (endTime) {
                        const { deliveryStartTime, deliveryWindowType } = this.parent;

                        if (deliveryWindowType !== WINDOW_TYPE.DELIVERY_TIME) return true;

                        if (!deliveryStartTime || !endTime) return true;

                        const [startHour, startMin] = deliveryStartTime.split(':').map(Number);
                        const [endHour, endMin] = endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;

                        return endMinutes > startMinutes;
                    }),
                serviceId: Yup.array().of(Yup.number()).notRequired(),
                consigneeId: Yup.number().nullable(),
                consigneeCompanyName: Yup.string().when('consigneeId', {
                    is: (val: any) => val !== null && val !== undefined,
                    then: (schema) => schema.required('Consignee company name is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
                consigneeAddress: Yup.object()
                    .shape({
                        fullAddress: Yup.string().required(t('ValidationMessages.Consignee_address_is_required')),
                        latitude: Yup.string().required(t('ValidationMessages.Consignee_latitude_is_required')),
                        longitude: Yup.string().required(t('ValidationMessages.Consignee_longitude_is_required')),
                        city: Yup.string().required('City is required'),
                        state: Yup.string().required('State is required'),
                        country: Yup.string().required('Country is required'),
                        postal: Yup.string().required('Postal code is required'),
                        streetNumber: Yup.string().required('Street number is required'),
                    })
                    .nullable()
                    .required('Consignee address is required'),
            }),
        [t, appearance?.timezone, pickupWindowEndDate]
    );

    const defaultValues = useMemo(
        () => ({
            deliverySensitivity: savedData?.deliverySensitivity || DATE_TYPE.OPEN,
            // Clear stale past dates — show null as default; Yup validates on submit
            deliveryDate: savedData?.deliveryDate || null,
            deliveryEndDate: savedData?.deliveryEndDate || null,
            deliveryWindowType: savedData?.deliveryWindowType || WINDOW_TYPE.OPEN,
            deliveryStartTime: savedData?.deliveryStartTime || null,
            deliveryEndTime: savedData?.deliveryEndTime || null,
            serviceId: (savedData?.serviceId as number[]) || [],
            limitedAccessDelivery: savedData?.limitedAccessDelivery || false,
            limitedAccessDeliveryType: savedData?.limitedAccessDeliveryType || '',
            limitedAccessDeliveryOther: savedData?.limitedAccessDeliveryOther || '',
            consigneeId: savedData?.consigneeId || null,
            consigneeCompanyName: savedData?.consigneeCompanyName || '',
            consigneeAddress: savedData?.consigneeAddress || null,
        }),
        [savedData, appearance?.timezone]
    );

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        trigger,
        clearErrors,
        getValues,
        formState: { errors, isSubmitted },
    } = useForm<IConsigneeDetailsForm>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues,
    });

    const deliverySensitivity = watch('deliverySensitivity');
    const deliveryWindowType = watch('deliveryWindowType');
    const deliveryDate = watch('deliveryDate');
    const watchConsigneeAddress = watch('consigneeAddress');
    const watchConsigneeFullAddress = watch('consigneeAddress')?.fullAddress;
    const watchConsigneeCompanyName = watch('consigneeCompanyName');
    const watchConsigneeId = watch('consigneeId');
    const watchServiceId = watch('serviceId');

    const selectedConsigneeBusinessHours = useMemo(() => {
        const entity = consignees?.find((c: any) => c.id === watchConsigneeId);
        const start = entity?.businessHoursStartTime ? String(entity.businessHoursStartTime).slice(0, 5) : null;
        const end = entity?.businessHoursEndTime ? String(entity.businessHoursEndTime).slice(0, 5) : null;

        return start && end ? `${start} – ${end}` : null;
    }, [watchConsigneeId, consignees]);

    useEffect(() => {
        const prev = prevDeliveryWindowType.current;
        prevDeliveryWindowType.current = deliveryWindowType;

        if (prev === undefined || prev === deliveryWindowType) return;

        const appointmentService = (serviceTypes?.DELIVERY || []).find((s: any) => s.slug === 'delivery_appointment_required');

        if (!appointmentService) return;

        const appointmentId: number = appointmentService.id;
        const shouldSelect = deliveryWindowType === 'appointment';
        const currentIds: number[] = getValues('serviceId') || [];
        const isAlreadySelected = currentIds.includes(appointmentId);

        if (shouldSelect && !isAlreadySelected) {
            setValue('serviceId', [...currentIds, appointmentId]);
        } else if (!shouldSelect && isAlreadySelected) {
            setValue(
                'serviceId',
                currentIds.filter((id) => id !== appointmentId)
            );
        }
    }, [deliveryWindowType]);

    useEffect(() => {
        if (savedData?.consigneeAddress) {
            const isValid = isShipperConsigneeAddressValid(savedData.consigneeAddress as any);

            if (!isValid || !savedData.consigneeId) {
                setIsEditId(true);
            }
        }
    }, []);

    // ─── Delivery Additional Services ────────────────────────────────────────────

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const textReplacements = [{ original: 'Delivery Appointment Required', replacement: t('QuotingHub.Common.Delivery_Appointment') }];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements.find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const handleClose = useCallback(() => {
        setAction({ deliveryService: false, limitedAccess: false });
    }, []);

    const handleServiceChange = (e: any) => {
        let { checked, name, value } = e.target;
        value = parseInt(value);

        if (checked) {
            if (name === 'limited_access_delivery') {
                setAction({ ...action, limitedAccess: true });
            } else {
                setValue('serviceId', [...watchServiceId, value]);
            }
        } else {
            setValue(
                'serviceId',
                watchServiceId.filter((item: any) => item !== value)
            );

            if (name === 'limited_access_delivery') {
                setValue('limitedAccessDelivery', false);
                setValue('limitedAccessDeliveryType', '');
                setValue('limitedAccessDeliveryOther', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'limited_access_delivery') {
            setAction({ ...action, limitedAccess: true });
        }
    };

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('limitedAccessDelivery', true);
        setValue('limitedAccessDeliveryType', data.selectedLimitedAccessType);
        setValue('limitedAccessDeliveryOther', data.limitedAccessOther);

        const limitedAccessId = serviceTypes?.DELIVERY?.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        if (limitedAccessId && !watchServiceId.includes(Number(limitedAccessId))) {
            setValue('serviceId', [...watchServiceId, Number(limitedAccessId)]);
        }

        handleClose();
    };

    const handleCloseServiceModal = useCallback(() => {
        setAction({ deliveryService: false, limitedAccess: false });
    }, []);

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    !excludedServices.includes(val.slug) && (val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`delivery_service_${val.id}`}>
                            <CheckBox
                                id={`delivery_service_${val.id}`}
                                onChangeFunc={handleServiceChange}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                            />
                            <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                                {val.slug === 'delivery_appointment_required' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`delivery_service_${val.id}`} className="cursor-pointer truncate">
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                        <p className="truncate" ref={serviceRefName}>
                                            <label htmlFor={`delivery_service_${val.id}`} className="cursor-pointer">
                                                {getReplacedText(val.name)}
                                            </label>
                                        </p>
                                    </TruncateTextCmp>
                                )}
                            </div>
                            {watchServiceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId]
    );

    const onSubmit = (formData: IConsigneeDetailsForm) => {
        const updatedFormData = {
            ...formData,
            deliveryDate: formData.deliveryDate ? moment(formData.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
            deliveryEndDate: formData.deliveryEndDate ? moment(formData.deliveryEndDate).format('YYYY-MM-DD 00:00:00') : null,
        };

        onSaveNext(updatedFormData, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS);
    };

    // Sidebar persist — same payload shape as onSubmit (both delivery dates are already
    // null-guarded there), written to the slice without validating or completing the step.
    useRegisterStepperPersist(() => {
        const formData = getValues();

        onPersist(
            {
                ...formData,
                deliveryDate: formData.deliveryDate ? moment(formData.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
                deliveryEndDate: formData.deliveryEndDate ? moment(formData.deliveryEndDate).format('YYYY-MM-DD 00:00:00') : null,
            },
            ADD_QUOTE_KEYS.CONSIGNEE_DETAILS
        );
    });

    // Scroll the first invalid field into view when Next is blocked. RHF's onInvalid callback is
    // the only place the freshly-computed errors are available synchronously — a closure over
    // formState.errors would still hold the value from before validation ran.
    const onInvalid = useCallback((formErrors: any) => {
        scrollToFirstFormError(collectFormErrorPaths(formErrors));
    }, []);

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) return false;

        handleSubmit(onSubmit)();

        return true;
    }, [trigger, handleSubmit]);

    const handleBack = useCallback(() => {
        const formData = getValues();
        const updatedFormData = {
            ...formData,
            deliveryDate: formData.deliveryDate ? moment(formData.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
            deliveryEndDate: formData.deliveryEndDate ? moment(formData.deliveryEndDate).format('YYYY-MM-DD 00:00:00') : null,
        };
        onBack(updatedFormData, ADD_QUOTE_KEYS.CONSIGNEE_DETAILS);
    }, [onBack, getValues]);

    useEffect(() => {
        setHeaderChildren(<StepperAction handleBack={handleBack} handleNext={handleSubmit(handleNext, onInvalid)} nextButtonText={t('Common.Next')} isNextButtonDisabled={isEditId} />);

        return () => setHeaderChildren(null);
    }, [isEditId]);

    // A create blocked on a later step by a stale delivery date lands the user back here (see
    // blockOnPastDates in useSaveNextStep). The form mounts pristine, so run yup immediately —
    // that paints the red border on the delivery date — and scroll it into view. The flag is
    // cleared first so this fires exactly once per bounce.
    useEffect(() => {
        if (revalidateStep !== ADD_QUOTE_KEYS.CONSIGNEE_DETAILS) return;

        dispatch(setAddQuoteRevalidateStep(null));

        trigger().then((isValid) => {
            // Targeted rather than derived from formState.errors: those are not readable
            // synchronously here (the closure predates the validating render), and the only reason
            // this path runs is a date the user has to fix.
            if (!isValid) scrollToFirstFormError(['deliveryDate']);
        });
    }, [revalidateStep]);

    // Delivery sensitivity: switching flexible → fixed clears deliveryEndDate; switching to open clears both dates
    const handleDeliverySensitivityChange = (onChange: any) => (value: string) => {
        onChange(value);

        if (value !== DATE_TYPE.FLEXIBLE) {
            setValue('deliveryEndDate', null);

            if (isSubmitted) clearErrors(['deliveryEndDate', 'deliveryDate']);
        }

        if (value === DATE_TYPE.OPEN) {
            setValue('deliveryDate', null);
        }
    };

    // Delivery date: clear deliveryEndDate if it's before new deliveryDate
    const handleDeliveryDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);

        const currentDeliveryEndDate = getValues('deliveryEndDate');

        if (currentDeliveryEndDate && value && moment(currentDeliveryEndDate).isBefore(moment(value), 'day')) {
            setValue('deliveryEndDate', null);

            if (isSubmitted) trigger('deliveryEndDate');
        }
    };

    // Delivery window type: switching to 'open' clears time fields
    const handleDeliveryWindowTypeChange = (val: string, onChange: any) => () => {
        onChange(val);

        if (val === WINDOW_TYPE.OPEN) {
            setValue('deliveryStartTime', null);
            setValue('deliveryEndTime', null);
        } else if (val === WINDOW_TYPE.APPOINTMENT) {
            setValue('deliveryEndTime', null);
        }

        if (isSubmitted) {
            trigger('deliveryStartTime');
            trigger('deliveryEndTime');
        }
    };

    const handleTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void, previousValue: string) => {
        const formatted = formatTimeInput(event.target.value, previousValue);
        onChange(formatted);
    };

    // ---------- Consignee address handlers ----------

    const triggerConsigneeValidation = () => {
        if (isSubmitted) {
            trigger('consigneeCompanyName');
            trigger('consigneeAddress');
        }
    };

    const resetConsigneeForm = () => {
        setValue('consigneeCompanyName', '');
        setValue('consigneeAddress', null);
    };

    const handleConsigneeChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const data = consignees.find((c: any) => c.id === value.value);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);
                const deliveryServices = (data.deliveryAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
                setValue('serviceId', deliveryServices);
                triggerConsigneeValidation();

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditId(true);
                }
            }
        } else {
            onChange(null);
            resetConsigneeForm();
        }
    };

    const handleGoogleAddressSelect = (addressData: any) => {
        setValue('consigneeId', null);
        // Google establishments carry their business name (parseGooglePlaceDetails sets
        // companyName from place.name); seed the name field with it so the Add New Consignee
        // form opens pre-filled instead of blank. Plain street addresses return '', which keeps
        // the previous clear-it behaviour. Matches WALHQ's "company name from address search".
        setValue('consigneeCompanyName', addressData?.companyName ?? '');
        setValue('consigneeAddress', {
            fullAddress: addressData?.fullAddress ?? '',
            address1: addressData?.address1 ?? '',
            city: addressData?.city ?? '',
            country: addressData?.country ?? '',
            countryCode: addressData?.countryCode ?? '',
            latitude: addressData?.latitude ?? '',
            longitude: addressData?.longitude ?? '',
            postal: addressData?.postal ?? '',
            state: addressData?.state ?? '',
            stateCode: addressData?.stateCode ?? '',
            streetNumber: addressData?.streetNumber ?? '',
            timezone: addressData?.timezone ?? '',
            additionalServiceIds: addressData?.additionalServiceIds ?? [],
        });
        triggerConsigneeValidation();
        setIsEditId(true);
    };

    const handleAutocompleteChange = (value: any) => {
        if (!value) {
            if (!watchConsigneeFullAddress) setValue('consigneeAddress', null);

            return;
        }

        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchConsigneeFullAddress) setValue('consigneeAddress', null);

            return;
        }

        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('consigneeAddress', null);

            return;
        }

        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            setValue('consigneeAddress', {
                ...(watchConsigneeAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchConsigneeAddress?.address1 ?? '',
                city: value.city ?? watchConsigneeAddress?.city ?? '',
                country: value.country ?? watchConsigneeAddress?.country ?? '',
                countryCode: value.countryCode ?? watchConsigneeAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchConsigneeAddress?.latitude ?? '',
                longitude: value.longitude ?? watchConsigneeAddress?.longitude ?? '',
                postal: value.postal ?? watchConsigneeAddress?.postal ?? '',
                state: value.state ?? watchConsigneeAddress?.state ?? '',
                stateCode: value.stateCode ?? watchConsigneeAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchConsigneeAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchConsigneeAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchConsigneeAddress?.additionalServiceIds ?? [],
            });
        }
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            const currentId = getValues('consigneeId');
            setPreEditId(currentId ? +currentId : null);
            setIsEditId(true);
        } else if (value === 'delete_id') {
            setDeleteAction({ mode: 'delete', consignee: true, isContact: false, contactId: null });
        } else if (value === 'switch_id') {
            setValue('consigneeId', null);
            resetConsigneeForm();
        }

        triggerConsigneeValidation();
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        setValue('consigneeId', null);
        resetConsigneeForm();
    };

    const handleEditConsignee = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);

        if (!isEdit) {
            const isValidId = preEditId && preEditId === watchConsigneeId;

            if (isValidId) {
                setPreEditId(null);
            }
            setValue('consigneeId', null);
            resetConsigneeForm();
        }
    };

    const handleSaveConsignee = (data: any) => {
        setIsEditId(false);
        setValue('consigneeId', data.id);
        setValue('consigneeCompanyName', data.companyName);
        setValue('consigneeAddress', data);
        // Auto-select delivery additional services saved with this address.
        // Fall back to `additionalServiceIds` (raw DB field) in case the
        // list endpoint or a future caller omits the renamed field.
        const deliveryServices = (data.deliveryAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
        setValue('serviceId', deliveryServices);
        setPreEditId(null);
        clearErrors('consigneeAddress');
        clearErrors('consigneeCompanyName');
    };

    useEffect(() => {
        if (watchConsigneeId) {
            const currentLatitude = getValues('consigneeAddress')?.latitude;

            if (watchConsigneeAddress && currentLatitude) return;

            const data = consignees.find((c: any) => c.id === watchConsigneeId);

            if (data) {
                setValue('consigneeCompanyName', data.companyName);
                setValue('consigneeAddress', data);
                // Auto-select delivery additional services saved with this address.
                // Fall back to `additionalServiceIds` (raw DB field) in case the
                // list endpoint or a future caller omits the renamed field.
                const deliveryServices = (data.deliveryAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
                setValue('serviceId', deliveryServices);
            }
        } else {
            const currentLatitude = getValues('consigneeAddress')?.latitude;

            if (!currentLatitude && !watchConsigneeAddress) resetConsigneeForm();
        }
    }, [consignees, watchConsigneeId]);

    const handleConsigneeInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            handleSearchConsignee(e);
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        setDeleteAction(INITIAL_DELETE_ACTION);

        if (closeStatus) {
            setIsDeleting(true);
            const idToDelete = watchConsigneeId ? +watchConsigneeId : null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            setValue('consigneeAddress', null);
                            setValue('consigneeId', null);
                            setValue('consigneeCompanyName', '');

                            dispatch(setConsignees(consignees.filter((c: any) => c.id !== idToDelete)));
                            dispatch(setConsigneeOptions(consigneeOptions.filter((o: any) => o.value !== idToDelete)));
                        }
                    })
                    .catch(async (error: any) => {
                        WalToast.error(error?.response?.data?.message || t('ValidationMessages.Consignee_delete_failed'));
                    })
                    .finally(() => setIsDeleting(false));
            } else {
                setIsDeleting(false);
            }
        }
    };

    return (
        <>
            {/* Consignee Address */}
            <div>
                {!isEditId && (
                    <div data-field-anchor="consigneeAddress consigneeId consigneeCompanyName" className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <label className="stepper-label-common">
                                {t('QuotingHub.CreateQuote.Consignee_Details.Consignee')}
                                <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                            </label>
                        </div>
                        <div className="flex-1 space-y-3">
                            <ShipperConsigneeAutocomplete
                                control={control}
                                isShipper={false}
                                isEditId={isEditId}
                                watchCompanyName={watchConsigneeCompanyName}
                                watchShipperAddress={watchConsigneeAddress}
                                watchShipperFullAddress={watchConsigneeFullAddress}
                                shipperOptions={shipperOptions}
                                consigneeOptions={consigneeOptions}
                                errors={errors}
                                handleShipperChange={handleConsigneeChange}
                                handleCreateNew={handleCreateNew}
                                handleCardActionType={handleCardActionType}
                                onInputChange={handleConsigneeInputChange}
                                fieldName="consigneeId"
                                isQuoteCreateForm={true}
                                showDeleteOption={!!watchConsigneeId}
                                handleGoogleAddressSelect={handleGoogleAddressSelect}
                                watchShipperConsigneeId={watchConsigneeId}
                            />
                            <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">
                                <MapBox
                                    draggable={false}
                                    address={watchConsigneeAddress?.fullAddress ? watchConsigneeAddress : null}
                                    id="consigneeMap"
                                    onReverseAddressFunc={handleAutocompleteChange}
                                    className="rounded-lg bg-white h-full border border-utilityGray200"
                                    isDefaultSatelliteView={true}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {isEditId && (
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditConsignee}
                        id={watchConsigneeId ? +watchConsigneeId : undefined}
                        handleSaveShipper={handleSaveConsignee}
                        isShipper={false}
                        defaultCompanyName={watchConsigneeCompanyName || ''}
                        defaultAddress={(watchConsigneeAddress as any) || null}
                        showAddressIncompleteWarning
                    />
                )}

                {deleteAction.mode === 'delete' && (
                    <DeleteModal
                        handleClose={handleModalClose}
                        moduleTitle={t('Orders.remove_from_wal_hq', { companyName: watchConsigneeCompanyName || '' })}
                        moduleName={t('Common.Consignee')}
                        isDeleteLoading={isDeleting}
                    />
                )}
            </div>

            {/* Delivery Sensitivity */}
            <div data-field-anchor="deliverySensitivity" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Delivery_Sensitivity')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="deliverySensitivity"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {DELIVERY_DATE_TYPE_OPTIONS.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={() => handleDeliverySensitivityChange(onChange)(option.value)}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Delivery Date */}
            <div data-field-anchor="deliveryDate deliveryEndDate" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Delivery Date</label>
                </div>
                {deliverySensitivity === 'flexible' ? (
                    <div className="w-full flex items-center gap-3">
                        <Controller
                            name="deliveryDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    inputName="deliveryDate"
                                    className="form_control"
                                    errorText={!!error}
                                    placeholder="Start date"
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={minDeliveryDate}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={handleDeliveryDateChange(onChange)}
                                    parentClassName="flex-1"
                                    isDisplayIcon={true}
                                />
                            )}
                        />
                        <Controller
                            name="deliveryEndDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    inputName="deliveryEndDate"
                                    className="form_control"
                                    errorText={!!error}
                                    placeholder="End date"
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={deliveryDate ? createDateWithoutTimezoneConversion(moment(deliveryDate)) : minDeliveryDate}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={(e: any) => {
                                        onChange(e.value);

                                        if (isSubmitted) {
                                            trigger('deliveryDate');
                                        }
                                    }}
                                    parentClassName="flex-1"
                                    isDisplayIcon={true}
                                />
                            )}
                        />
                    </div>
                ) : (
                    <Controller
                        name="deliveryDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                label=""
                                inputName="deliveryDate"
                                className="form_control"
                                errorText={!!error}
                                placeholder="Select delivery date"
                                dateFormat="dddd, MMMM Do, YYYY"
                                minDate={minDeliveryDate}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                onChangeFunc={handleDeliveryDateChange(onChange)}
                                parentClassName="w-full"
                                isDisplayIcon={true}
                                disabled={deliverySensitivity === 'open'}
                            />
                        )}
                    />
                )}
            </div>

            {/* Delivery Window */}
            <div data-field-anchor="deliveryWindowType deliveryStartTime deliveryEndTime" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">Delivery Window</label>
                </div>
                <div className="flex gap-x-2 flex-1 flex-col w-full">
                    <div className="flex items-center flex-1 gap-3">
                        <Controller
                            name="deliveryWindowType"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectBox
                                    parentClassName="w-[160px]"
                                    options={DELIVERY_WINDOW_TYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                                    value={value ? DELIVERY_WINDOW_TYPE_OPTIONS.find((opt) => value === opt.value) : null}
                                    onChangeFunc={(selectedOption: any) => handleDeliveryWindowTypeChange(selectedOption?.value, onChange)()}
                                    isError={!!error}
                                    placeholder="Select"
                                />
                            )}
                        />

                        {deliveryWindowType !== 'open' && (
                            <Controller
                                name="deliveryStartTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="deliveryStartTime"
                                        placeholder="HH:MM"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}

                        {deliveryWindowType === WINDOW_TYPE.DELIVERY_TIME && (
                            <Controller
                                name="deliveryEndTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="deliveryEndTime"
                                        placeholder="HH:MM"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}
                    </div>
                    {DELIVERY_WINDOW_TOOLTIP[deliveryWindowType] && (
                        <p className="text-xs font-normal text-grayLight600 leading-[18px] italic mt-2 mb-2">
                            {deliveryWindowType === WINDOW_TYPE.OPEN && selectedConsigneeBusinessHours
                                ? DELIVERY_WINDOW_TOOLTIP[deliveryWindowType].replace('business hours', `business hours (${selectedConsigneeBusinessHours})`)
                                : DELIVERY_WINDOW_TOOLTIP[deliveryWindowType]}
                        </p>
                    )}
                </div>
            </div>

            {/* Delivery Additional Services */}
            <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('QuotingHub.Common.Delivery_Additional_Services')}
                        <div className="cursor-pointer" onClick={() => setAction((prev) => ({ ...prev, deliveryService: true }))}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.DELIVERY || [])}</div>
                </div>
            </div>

            {/* Modals */}
            {action.deliveryService && <AdditionalServiceModal handleClose={handleCloseServiceModal} services={serviceTypes?.DELIVERY || []} title={`Delivery ${t('Common.Additional_Services')}`} />}
            {action.limitedAccess && (
                <LimitedAccessDeliveryModal
                    handleClose={handleClose}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={
                        {
                            limitedAccessType: getValues('limitedAccessDeliveryType'),
                            limitedAccessOther: getValues('limitedAccessDeliveryOther'),
                        } as ILimitedAccessDeliveryDataForm
                    }
                />
            )}
        </>
    );
};

export default ConsigneeDetails;
